import { flow, getRoot, Instance, types } from "mobx-state-tree";

import config from "../config";
import { BookmarkStore } from "./Bookmarks";
import PrecipSensor, {
  IPrecipSensor,
  isPrecipSensor,
  PrecipSensor_,
} from "./PrecipSensor";
import { IRoot } from "./Root";
import {
  SelectedPane,
  SelectedPrecipSensor,
  SelectedSensor,
  SelectedStreamSensor,
} from "./SelectedSensor";
import {
  ISensorDisplayPrecip,
  ISensorDisplayStream,
  SensorDisplay,
  SensorDisplayPrecip,
  SensorDisplayStream,
} from "./SensorDisplay";
import {
  isStreamSensor,
  IStreamSensor,
  StreamSensor,
  StreamSensor_,
} from "./StreamSensor";

const {
  precipIntervals: { default: defaultPrecipInterval },
  streamPeriods: { default: defaultStagePeriod },
  agencyPassList
} = config;

const { map, maybe, model, optional, union } = types;

export const Sensor = union(PrecipSensor, StreamSensor);
export type Sensor_ = PrecipSensor_ | StreamSensor_;
export type ISensor = IPrecipSensor | IStreamSensor;

const wait = () => new Promise((resolve) => setTimeout(resolve, 30000));
const invalidSensorsLogged = new Set<string>();

const matchesAgencyPassList = (owner: string) => {
  if (!agencyPassList) {
    return true;
  }

  const normalizedOwner = owner.toLowerCase();
  return agencyPassList.some((agency) =>
    normalizedOwner.includes(agency.toLowerCase())
  );
};

export const Sensors = model({
  display: maybe(SensorDisplay),
  selected: maybe(SelectedSensor),
  sensors: map(Sensor),
  loading: true,
  bookmarks: optional(BookmarkStore, {}),
})
  .named("SensorStore")
  .views((self) => ({
    get sensorList(): ISensor[] {
      return Array.from(self.sensors.values());
    },
  }))
  .views((self) => ({
    get precipSensors(): IPrecipSensor[] {
      return self.sensorList.filter(isPrecipSensor);
    },
    get streamSensors(): IStreamSensor[] {
      const sensors = self.sensorList.filter(isStreamSensor);
      if (
        SensorDisplayStream.is(self.display) &&
        self.display.measure === "discharge"
      ) {
        return sensors.filter((s) => s.hasDischarge);
      }

      return sensors;
    },
    get bookmarkedSensors() {
      return self.sensorList.filter((sensor) =>
        self.bookmarks.ids.has(sensor.id)
      );
    },
  }))
  .actions((self) => ({
    putSensor(sensor: ISensor) {
      self.sensors.put(sensor);
    },
    setSensor(
      sensor: ISensor,
      pane: Instance<typeof SelectedPane> = "summary",
      openPanel: boolean = false
    ) {
      if (self.display) {
        const root = getRoot<IRoot>(self);
        root.layers.clearSelectedFeature();
        root.layers.lightning?.clear();

        if (openPanel) {
          root.menus.openPanel("sensors");
        }

        if (sensor.type === "precip") {
          if (SensorDisplayStream.is(self.display)) {
            // i.e. we're switching from stream to precip
            const { measure } = self.display;

            self.display = {
              type: "precip",
              interval: defaultPrecipInterval,
              measure: measure === "stage" ? "total" : "intensity",
            };
          }

          self.selected = SelectedPrecipSensor.create({
            type: "precip",
            sensor: sensor.id,
            display: { ...self.display },
            pane,
          });
        } else {
          if (SensorDisplayPrecip.is(self.display)) {
            // i.e. we're switching from precip to stream
            const { measure } = self.display;

            self.display = {
              type: "stream",
              period: defaultStagePeriod,
              measure: measure === "total" ? "stage" : "discharge",
            };
          }

          self.selected = SelectedSensor.create({
            type: "stream",
            sensor: sensor.id,
            display: { ...self.display },
            pane,
          });
        }
      } else {
        console.log("self.display is undefined");
      }
    },

    clearSensor() {
      self.selected = undefined;
    },
  }))

  .actions((self) => ({
    setLoading(loading: boolean) {
      self.loading = loading;
    },
    clearDisplay() {
      self.display = undefined;
      self.selected = undefined;
    },
    displayPrecipWithInterval(interval: string) {
      if (SensorDisplayPrecip.is(self.display)) {
        self.display.interval = interval;
      } else if (self.display !== undefined) {
        const measure =
          self.display.measure === "stage" ? "total" : "intensity";
        self.display = { type: "precip", interval, measure };
      } else {
        self.display = { type: "precip", interval, measure: "total" };
      }

      if (SelectedPrecipSensor.is(self.selected)) {
        self.selected.display.interval = interval;
      } else if (SelectedStreamSensor.is(self.selected)) {
        const { site } = self.selected.sensor;
        const sensor = self.precipSensors.find((s) => s.site.startsWith(site));
        if (sensor) {
          self.selected = SelectedPrecipSensor.create({
            type: "precip",
            sensor: sensor.id,
            display: { ...self.display },
            pane: self.selected?.pane,
          });
        } else {
          self.clearSensor();
        }
      }
    },
    displayStreamWithPeriod(period: string) {
      if (SensorDisplayStream.is(self.display)) {
        self.display.period = period;
      } else if (self.display !== undefined) {
        const measure =
          self.display.measure === "total" ? "stage" : "discharge";
        self.display = { type: "stream", period, measure };
      } else {
        self.display = { type: "stream", period, measure: "stage" };
      }

      if (SelectedStreamSensor.is(self.selected)) {
        self.selected.display.period = period;
      } else if (SelectedPrecipSensor.is(self.selected)) {
        const { site } = self.selected.sensor;
        const sensor = self.streamSensors.find((s) => s.site.startsWith(site));
        if (sensor) {
          self.selected = SelectedStreamSensor.create({
            type: "stream",
            sensor: sensor.id,
            display: { ...self.display },
            pane: self.selected?.pane,
          });
        } else {
          self.clearSensor();
        }
      }
    },
    displayPrecipWithMeasure(measure: ISensorDisplayPrecip["measure"]) {
      if (SensorDisplayPrecip.is(self.display)) {
        self.display.measure = measure;
      } else {
        self.display = {
          type: "precip",
          interval: defaultPrecipInterval,
          measure,
        };
      }
      if (SelectedPrecipSensor.is(self.selected)) {
        self.selected.display.measure = measure;
      } else {
        self.clearSensor();
      }
    },
    displayStreamWithMeasure(measure: ISensorDisplayStream["measure"]) {
      if (SensorDisplayStream.is(self.display)) {
        self.display.measure = measure;
      } else {
        self.display = { type: "stream", period: "last", measure };
      }

      if (SelectedStreamSensor.is(self.selected)) {
        self.selected.display.measure = measure;
        if (measure === "discharge" && !self.selected.sensor.hasDischarge) {
          self.clearSensor();
        }
      } else {
        self.clearSensor();
      }
    },
  }))
  .actions((self) => ({
    fetch_: async (type: "precip" | "stream", path: string) => {
      const rootApiUrl = process.env.REACT_APP_API_URL || "https://jefulleralert.com/api"

      const url = rootApiUrl + path;
      const fetchResponse = await fetch(url);
      if (!fetchResponse.ok) {
        console.error(`${url} ${fetchResponse.status} ${fetchResponse.statusText}`);
        return
      }

      try {
        const json = await fetchResponse.json();
        const sensors = Object.entries(json.data) as [string, ISensor][];
        for (const [id, sensor] of sensors) {
          if (!matchesAgencyPassList(sensor.owner)) {
            continue;
          }
          try {
            if (type === "precip") {
              self.putSensor({ ...(sensor as IPrecipSensor), id, type: "precip" });
            } else {
              self.putSensor({ ...(sensor as IStreamSensor), id, type: "stream" });
            }
          } catch {
            // Skip malformed sensor payloads so one bad record does not block updates.
            const invalidId = `${type}:${id}`;
            if (!invalidSensorsLogged.has(invalidId)) {
              invalidSensorsLogged.add(invalidId);
              console.warn(`Skipping invalid ${type} sensor ${id}`);
            }
          }
        }
      } catch (error) {
        console.error(`Failed to fetch sensors from ${url}:`, error);
        return
      }
    },
  }))
  .actions((self) => ({
    fetch: flow(function* f() {
      while (true) {
        yield Promise.all([
          self.fetch_("precip", "/precip.json"),
          self.fetch_("stream", "/stream.json"),
        ]);
        self.loading = false;
        yield wait();
      }
    }),
  }));
